import React, { useState, useEffect, useReducer, useRef, useMemo } from "react";
import {
  Tabs,
  Tab,
  Grid,
  Box,
  Card,
  IconButton,
  Button,
  Input,
  InputAdornment,
  Typography,
  Stack,
  Tooltip,
  Menu,
  MenuItem,
  Divider,
  List,
  ListItem,
  CircularProgress,
} from "@mui/material";
import dayjs from "dayjs";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { get } from "aws-amplify/api";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import {
  DataGrid,
  GridToolbarContainer,
  GridActionsCellItem,
  GridToolbarColumnsButton,
  GridRowModes,
  useGridApiRef,
} from "@mui/x-data-grid";
import { I18n } from "@aws-amplify/core";
import { GridToolbarExport, GridToolbarFilterButton } from "@mui/x-data-grid";
import ConfirmDeleteDialog from "popup/ConfirmDeleteDialog";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import currentUserToken from "CurrentUserToken";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import SearchIcon from "@mui/icons-material/Search";
import { useTheme } from "@mui/material/styles";
import { post } from "aws-amplify/api";
import { APIStatusDialog } from "components/APIStatusDialog";
import EncodeDialog from "popup/EncodeDialog";
import TemplateBox from "components/TemplateBox";
import { useNavigate } from "react-router-dom"; // Import useNavigate for navigation
import { useLocation } from "react-router-dom";
import { DataGridPro } from "@mui/x-data-grid-pro/DataGridPro";
import NewAccountAssignationDialog from "popup/NewAccountAssignationDialog";
import EncodingSettingsMenu from "../components/EncodingSettingsMenu";
import PatternSettingsMenu from "../components/PatternSettingsMenu";
import EventHistorySettingsMenu from "../components/EventHistorySettingsMenu";
import { itIT } from "@mui/x-data-grid/locales";
import { deDE } from "@mui/x-data-grid/locales";
import ErrorDialog from "popup/ErrorDialog";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import UnrecMailPopup from "popup/UnrecognizedMailPopup";
import { alarmColumns } from "layouts/dashboard/components/Contacts/columns";
import {
  DarkMode,
  EmailOutlined,
  EmailRounded,
  EmailSharp,
  EmailTwoTone,
  RefreshOutlined,
  FiberManualRecord
} from "@mui/icons-material";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import AddNewLinePopup from "popup/AddNewLinePopup";
import { set } from "date-fns";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import EditMapEmailPopup from "popup/EditMapEmailPopup";
const COLUMN_VISIBILITY_KEY = "columnVisibility_unrecognized";

const reducer = (lastState, action) => {
  if (action.type === "DATA_RECEIVED") {
    const audit = action.value;
    if (Array.isArray(audit)) {
      audit.forEach((element) => {
        if (typeof element.timestamp === "number") {
          element.timestamp = dayjs(element.timestamp).format(
            "YYYY-MM-DD HH:mm"
          );
        }
      });
      return { audit: audit, fetched: true };
    }
  }
  return lastState;
};

function EmailMapping() {
  const [state, dispatch] = useReducer(reducer, {
    audit: [],
    fetched: false,
  });
  const [apiStatus, setApiStatus] = useState({
    open: false,
    status: "",
    message: "",
  });
  const apiRef = useGridApiRef();
  const [searchText, setSearchText] = useState("");
  const theme = useTheme();
  const [language, setLanguage] = useState(
    localStorage.getItem("language") || "en"
  );
  const [tableParentIsMD, setTableParentIsMD] = useState(false);
  const [languages, setLanguages] = useState([]);
  const toolbarRef = useRef(null);
  const [filters, setFilters] = useState({});
  const [totalCount, setTotalCount] = useState(0);
  const [resizeThrottle, setResizeThrottle] = useState(60);
  const [rowModesModel, setRowModesModel] = useState({});
  const [selectedRows, setSelectedRows] = useState([]);
  const [contextMenu, setContextMenu] = useState(null);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({});
  const [openMapDialog, setOpenMapDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectMapEmailDelete, setSelectMapEmailDelete] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const parentElement = toolbarRef.current;
    if (!parentElement) {
      console.error("toolbarRef is not correctly attached to any DOM element.");
      return;
    }
    const checkSize = (width) => {
      const breakpoint = 960; // 960px is md
      setTableParentIsMD(width < breakpoint);
    };
    const resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        checkSize(entry.contentRect.width);
      }
    });
    resizeObserver.observe(parentElement);
    checkSize(parentElement.offsetWidth);
    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    const savedColumnVisibility = localStorage.getItem(COLUMN_VISIBILITY_KEY);
    if (savedColumnVisibility) {
      setColumnVisibilityModel(JSON.parse(savedColumnVisibility));
    } else {
      setColumnVisibilityModel({
        alarm_id: false,
      });
    }
  }, []);

  const handleColumnVisibilityChange = (newModel) => {
    setColumnVisibilityModel(newModel);
    localStorage.setItem(COLUMN_VISIBILITY_KEY, JSON.stringify(newModel));
  };

  const tlmGetSystemMap = async () => {
    try {
      const restOperation = get({
        apiName: "TelemonitoringAPI",
        path: "/systemMap/get",
        options: {
          headers: {
            "Content-Type": "application/json",
            Authorization: `${await currentUserToken()}`,
          },
        },
      });
      const { body } = await restOperation.response;
      const response = await body.json();
      dispatch({ type: "DATA_RECEIVED", value: response });
    } catch (error) {
      console.error("Error during the API call: ", error);
    }
  };

  useEffect(() => {
    tlmGetSystemMap();
  }, []);

  useEffect(() => {
    const tlmGetLanguagesAPI = async () => {
      try {
        const restOperation = get({
          apiName: "TelemonitoringAPI",
          path: "/languages",
          options: {
            headers: {
              "Content-Type": "application/json",
              Authorization: `${await currentUserToken()}`,
            },
            queryParams: {},
          },
        });
        const { body } = await restOperation.response;
        const response = await body.json();

        setLanguages(response);
      } catch (error) {
        console.error("Error during the API call:", error);
      }
    };
    tlmGetLanguagesAPI();
  }, []);

  const tlmPostNewSystemMap = async (formData) => {
    if (formData.rule === "email") {
      formData.search_pattern = formData.email_address;
    } else if (formData.rule === "subject") {
      formData.search_pattern = formData.word_key;
    }

    try {
      const restOperation = post({
        apiName: "TelemonitoringAPI",
        path: "/systemMap/new",
        options: {
          headers: {
            "Content-Type": "application/json",
            Authorization: `${await currentUserToken()}`,
          },
          body: {
            search_pattern: formData.search_pattern,
            tlm_asset_id: formData.tlm_asset_id,
            rule: formData.rule,
            delay: formData.delay,
            flag: formData.flag,
          },
        },
      });
      return restOperation;
    } catch (error) {
      throw error;
    }
  };

  const tlmPostDeleteSystemMap = async (row) => {
    try {
      const restOperation = get({
        apiName: "TelemonitoringAPI",
        path: "/systemMap/delete",
        options: {
          headers: {
            "Content-Type": "application/json",
            Authorization: `${await currentUserToken()}`,
          },
          queryParams: {
            system_map_id: row.id,
          },
        },
      });
      return restOperation;
    } catch (error) {
      console.error("Error during API call:", error);
    }
  };

  const tlmPostSystemMapChanges = async (formData) => {
    if (formData.rule === "email") {
      formData.search_pattern = formData.email_address;
    } else if (formData.rule === "subject") {
      formData.search_pattern = formData.word_key;
    }
    //If email does not have a flag, set it to false
    if (formData.flag === null) {
      formData.flag = false;
    }

    try {
      const restOperation = post({
        apiName: "TelemonitoringAPI",
        path: "/systemMap/edit",
        options: {
          headers: {
            "Content-Type": "application/json",
            Authorization: `${await currentUserToken()}`,
          },
          queryParams: {
            system_map_id: formData.id,
          },
          body: {
            search_pattern: formData.search_pattern,
            tlm_asset_id: formData.tlm_asset_id,
            rule: formData.rule,
            delay: formData.delay,
            flag: formData.flag,
          },
        },
      });
      return restOperation;
    } catch (error) {
      throw error;
    }
  };

  const columns = useMemo(() => {
    return [
      {
        field: "search_pattern",
        headerName: I18n.get("Search Pattern"),
        width: 450,
        editable: false,
      },
      {
        field: "local_monitoring_system_manufacturer",
        headerName: I18n.get("Monitoring Manufacturer"),
        width: 250,
        editable: false,
      },
      {
        field: "local_monitoring_system_type",
        headerName: I18n.get("Type"),
        width: 200,
        editable: false,
      },
      {
        field: "system_technology",
        headerName: I18n.get("Technology"),
        width: 200,
        editable: false,
      },
      {
        field: "store_name",
        headerName: I18n.get("Store Name"),
        width: 200,
        editable: false,
      },
      {
        field: "store_management",
        headerName: I18n.get("Store Management"),
        width: 200,
        editable: false,
      },
      {
        field: "offline",
        headerName: I18n.get("Offline"),
        width:  100,
        editable: false,
        renderCell: (params) => (
          <FiberManualRecord
            sx={{
              color: params.value ? "red" : "green",
              height: "1.5rem",
              width: "1.5rem",
              margin: "0.35rem",
            }}
          />
        )
      },
      {
        field: "shift",
        headerName: I18n.get("Shift"),
        width: 100,
        editable: false,
      },
      {
        field: "account_number",
        headerName: I18n.get("Account Number"),
        width: 200,
        editable: false,
      },
      {
        field: "account_name",
        headerName: I18n.get("Account Name"),
        width: 200,
        editable: false,
      },
      {
        field: "fsl_country",
        headerName: I18n.get("FSL Country"),
        width: 150,
        editable: false,
      },
      {
        field: "type_of_ip_connect",
        headerName: I18n.get("Type of IP Connect"),
        width: 200,
        editable: false,
      },
      {
        field: "fsl_ip_address",
        headerName: I18n.get("Ip Address"),
        width: 100,
        editable: false,
      },
      {
        field: "tlm_asset_id",
        headerName: I18n.get("Asset Id"),
        width: 200,
        editable: false,
      },
      {
        field: "id",
        headerName: I18n.get("Id"),
        width: 100,
        editable: false,
      },
      {
        field: "rule",
        headerName: I18n.get("Rule"),
        width: 100,
        editable: false,
      },
      {
        field: "delay",
        headerName: I18n.get("Delay"),
        width: 100,
        editable: false,
      },
      {
        field: "flag",
        headerName: I18n.get("Flag"),
        width: 100,
        editable: false,
      },
      {
        field: "fsl_url_link",
        headerName: I18n.get("FSL Url Link"),
        width: 200,
        renderCell: (params) => (
          <Typography
            component="a"
            href={`${params.value}`}
            sx={{
              fontSize: "medium",
              cursor: "pointer",
              textDecoration: "underline",
            }}
          >
            {params.value}
          </Typography>
        ),
      },
      {
        field: "number_of_monitored_devices",
        headerName: I18n.get("Monitored Devices"),
        width: 100,
        editable: false,
      },
      {
        field: "retailer_description",
        headerName: I18n.get("Retailer Description"),
        width: 200,
        editable: false,
      },
      {
        field: "service_territory_name",
        headerName: I18n.get("Territory Name"),
        width: 200,
        editable: false,
      },
      {
        field: "actions",
        headerName: I18n.get("Actions"),
        resizable: false,
        width: 100,
        renderCell: (params) => {
          return (
            <>
              <Tooltip title={I18n.get("Edit")}>
                <GridActionsCellItem
                  icon={<EditIcon />}
                  label={I18n.get("Edit")}
                  onClick={(event) => {
                    event.stopPropagation();
                    handleEditClick(params.row, false);
                  }}
                />
              </Tooltip>
              <Tooltip title={I18n.get("Delete")}>
                <GridActionsCellItem
                  icon={<DeleteIcon />}
                  label={I18n.get("Delete")}
                  onClick={(event) => {
                    event.stopPropagation();
                    handleDeleteClick(params.row, false);
                  }}
                />
              </Tooltip>
            </>
          );
        },
      },
    ];
  }, [rowModesModel]);

  const applyStoreFiltering = (value) => {
    let matchesAllFilters = true;
    if (searchText) {
      const searchTextLower = searchText.toLowerCase();
      matchesAllFilters =
        matchesAllFilters &&
        Object.values(value).some((val) =>
          String(val).toLowerCase().includes(searchTextLower)
        );
    }
    return matchesAllFilters;
  };
  const filteredRows = state.audit.filter(applyStoreFiltering);
  const filteredRowsCount = filteredRows.length;

  const handleRowSelection = (ids) => {
    setSelectedRows(ids);
  };

  const searchChanged = (event) => {
    setSearchText(event.target.value);
  };

  const handleRowContextMenu = (event) => {
    event.preventDefault();
    setContextMenu(
      contextMenu === null
        ? {
            mouseX: event.clientX - 2,
            mouseY: event.clientY - 4,
          }
        : null
    );
  };

  const handleEditClick = (row) => {
    setEmailContent(row);
    setOpenEditDialog(true);
  };

  const handleDeleteClick = (row) => {
    setSelectMapEmailDelete(row);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
  };

  const handleConfirmDelete = async () => {
    setOpenDeleteDialog(false);
    try {
      setApiStatus({
        open: true,
        status: "pending",
        message: I18n.get("Deleting email mapping"),
      });
      const restOperation = await tlmPostDeleteSystemMap(selectMapEmailDelete);
      const response = await restOperation.response;
      if (response.statusCode === 200) {
        setApiStatus({
          open: true,
          status: "success",
          message: I18n.get("Email mapping successfully deleted"),
        });
        dispatch({
          type: "DATA_RECEIVED",
          value: state.audit.filter(
            (row) => row.id !== selectMapEmailDelete.id
          ),
        });
      } else {
        setApiStatus({
          open: true,
          status: "error",
          message: I18n.get("Failed to delete email mapping"),
        });
      }
      setTimeout(() => {
        setApiStatus((prevState) => ({ ...prevState, open: false }));
      }, 2000);
    } catch (error) {
      setApiStatus({
        open: true,
        status: "error",
        message: I18n.get("Failed to delete email mapping"),
      });
      setTimeout(() => {
        setApiStatus((prevState) => ({ ...prevState, open: false }));
      }, 2000);
    }
  };

  const [emailContent, setEmailContent] = useState([]);

  const handleAddLine = () => {
    setOpenMapDialog(true);
  };

  const renderToolbar = () => {
    return (
      <div ref={toolbarRef}>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
          {" "}
          <Typography variant="h5" mt={0.5} mb={1}>
            {I18n.get("Email Mapping")} (
            {filters ? filteredRowsCount : totalCount})
          </Typography>
        </Stack>
        <Stack
          direction={tableParentIsMD ? "column" : "row"}
          alignItems="center"
          spacing={2}
          sx={{ pb: 2 }}
        >
          <Stack
            direction={tableParentIsMD ? "column" : "row"}
            spacing={2}
            sx={{ width: "100%" }}
          >
            <Button
              onClick={() => handleAddLine()} // Open the pop-up
              color="primary"
              variant="outlined"
              startIcon={<AddIcon />}
              fullWidth={tableParentIsMD}
            >
              {I18n.get("Add New Line")}
            </Button>
          </Stack>
          <Box
            sx={{
              ml: 2,
              flexGrow: 1,
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <Input
              type="text"
              placeholder={I18n.get("Search")}
              onChange={searchChanged}
              fullWidth={tableParentIsMD}
              endAdornment={
                <InputAdornment position="end">
                  <IconButton>
                    <SearchIcon />
                  </IconButton>
                </InputAdornment>
              }
            />
          </Box>
        </Stack>
      </div>
    );
  };

  function getBrowserName() {
    const userAgent = navigator.userAgent;
    if (userAgent.includes("Firefox")) {
      return "Firefox";
    } else if (userAgent.includes("Edg")) {
      return "Edge";
    } else if (userAgent.includes("Chrome")) {
      return "Chrome";
    }
  }
  const browser = getBrowserName();
  function getScreenDimension() {
    const screenHeight = window.screen.height;
    if (screenHeight >= 1440) {
      return "27inches";
    } else if (screenHeight >= 1080) {
      return "24inches";
    }
  }
  const screenInches = getScreenDimension();

  const [tableHeight, setTableHeight] = useState(0);
  useEffect(() => {
    const updateHeight = () => {
      const navbarHeight = document.getElementById("navbar");
      const footerHeight = document.getElementById("footer");
      const margin =
        browser === "Firefox" ? 200 : browser === "Chrome" ? 200 : 200;
      const availableHeight =
        document.documentElement.clientHeight -
        navbarHeight -
        footerHeight -
        margin;
      setTableHeight(availableHeight);
    };

    updateHeight();
    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, []);

  useEffect(() => {
    document.body.style.overflow = "hidden";
  }, []);

  const handleMapEmailSubmit = async (formData) => {
    try {
      setApiStatus({
        open: true,
        status: "pending",
        message: I18n.get("Mapping email"),
      });
      setOpenMapDialog(false);

      const restOperation = await tlmPostNewSystemMap(formData);
      const response = await restOperation.response;
      if (response.statusCode === 200) {
        setApiStatus({
          open: true,
          status: "success",
          message: I18n.get("Email successfully mapped"),
        });
        dispatch({
          type: "DATA_RECEIVED",
          value: state.audit.filter((row) => row.id !== formData.id),
        });
        tlmGetSystemMap();
      } else {
        setApiStatus({
          open: true,
          status: "error",
          message: I18n.get("Failed to map email"),
        });
      }
      setTimeout(() => {
        setApiStatus((prevState) => ({ ...prevState, open: false }));
      }, 1000);
    } catch (error) {
      setApiStatus({
        open: true,
        status: "error",
        message: I18n.get("Failed to map email"),
      });
      setTimeout(() => {
        setApiStatus((prevState) => ({ ...prevState, open: false }));
      }, 1000);
    }
  };

  const handleEditMapEmailSubmit = async (formData) => {
    try {
      setApiStatus({
        open: true,
        status: "pending",
        message: I18n.get("Editing email map"),
      });
      setOpenEditDialog(false);

      const restOperation = await tlmPostSystemMapChanges(formData);
      const response = await restOperation.response;
      if (response.statusCode === 200) {
        setApiStatus({
          open: true,
          status: "success",
          message: I18n.get("Email successfully edited"),
        });
        dispatch({
          type: "DATA_RECEIVED",
          value: state.audit.filter((row) => row.id !== formData.id),
        });
        tlmGetSystemMap();
      } else {
        setApiStatus({
          open: true,
          status: "error",
          message: I18n.get("Failed to edit map"),
        });
      }
      setTimeout(() => {
        setApiStatus((prevState) => ({ ...prevState, open: false }));
      }, 1000);
    } catch (error) {
      setApiStatus({
        open: true,
        status: "error",
        message: I18n.get("Failed to edit map"),
      });
      setTimeout(() => {
        setApiStatus((prevState) => ({ ...prevState, open: false }));
      }, 1000);
    }
  };
  const darkMode = localStorage.getItem("darkMode") === "true";
  return (
    <>
      <DashboardLayout>
        <DashboardNavbar
          absolute
          light
          language={language}
          setLanguage={setLanguage}
        />
        <Stack
          direction="row"
          spacing={1}
          sx={{ ml: 3, mt: 3, width: "400px" }}
        >
          <EncodingSettingsMenu />
          <PatternSettingsMenu />
          <EventHistorySettingsMenu />
        </Stack>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Box sx={{ p: 2, maxHeight: `${tableHeight}px` }}>
              {" "}
              <Card
                sx={{
                  p: 3,
                  maxHeight: `${tableHeight}px`,
                  overflow: "auto",
                }}
              >
                {renderToolbar()}
                {/* <div  style={{
                    height:
                        screenInches === "27inches"
                        ? browser === "Chrome"
                        ? 985
                        : browser === "Firefox"
                        ? 970
                        : 970
                        : browser === "Chrome" //24 pollici
                        ? 625
                        : browser == "Firefox"
                        ? 605
                        : 610,
                }}> */}
                <DataGridPro
                  sx={{
                    "& .MuiDataGrid-root, & .MuiDataGrid-footerContainer": {
                      minHeight: "2rem",
                    },
                    "&  .MuiDataGrid-cell ": {
                      cursor: "default",
                    },
                    borderColor: darkMode ? "#6B635A" : "",
                    "& .MuiDataGrid-cell, &.MuiDataGrid-columnHeaders": {
                      borderColor: darkMode ? "#625a52" : "",
                    },
                    "& .MuiDataGrid-columnSeparator": {
                      borderColor: darkMode ? "#625a52" : "",
                    },
                    "--DataGrid-rowBorderColor": darkMode ? "#625a52" : "",
                    minHeight: "20vh",
                    flexFlow: 1,
                    height: "100%",
                  }}
                  localeText={
                    language === "it"
                      ? itIT.components.MuiDataGrid.defaultProps.localeText
                      : language === "ge"
                      ? deDE.components.MuiDataGrid.defaultProps.localeText
                      : ""
                  }
                  onCellDoubleClick={(params, event) => {
                    // Prevent the default double-click behavior
                    event.defaultMuiPrevented = true;
                  }}
                  hideFooterRowCount
                  editMode="row"
                  rows={filteredRows}
                  columns={columns}
                  //disableRowSelectionOnClick
                  getRowId={(row) => row.id}
                  rowModesModel={rowModesModel}
                  onRowModesModelChange={(newModel) =>
                    setRowModesModel(newModel)
                  }
                  onRowSelectionModelChange={(newSelection) =>
                    handleRowSelection(newSelection)
                  }
                  initialState={{
                    density: "compact",
                    columns: {
                      columnVisibilityModel: {
                        tlm_asset_id: false,
                        id: false,
                      },
                    },
                    pinnedColumns: { right: ["actions"] },
                  }}
                  autoPageSize
                  apiRef={apiRef}
                  resizeThrottleMs={resizeThrottle}
                  loading={!state.fetched || resizeThrottle > 60}
                  onProcessRowUpdateError={(error) => console.log({ error })}
                  //columnVisibilityModel={columnVisibilityModel}
                  onColumnVisibilityModelChange={handleColumnVisibilityChange}
                  onSelectionModelChange={(newSelection) => {
                    const selectedRows = newSelection.map((id) =>
                      filteredRows.find((row) => row.tlm_asset_id === id)
                    );
                    setSelectedRows(selectedRows);
                  }}
                  slotProps={{
                    loadingOverlay: {
                      variant: "skeleton",
                      noRowsVariant: "skeleton",
                    },
                    row: {
                      onContextMenu: (e) => handleRowContextMenu(e),
                    },
                  }}
                  slots={{
                    toolbar: () => (
                      <GridToolbarContainer>
                        <GridToolbarColumnsButton />
                        <GridToolbarExport
                          printOptions={{ disableToolbarButton: true }}
                          csvOptions={{
                            fileName: "Email Mapping",
                            utf8WithBom: true,
                          }}
                        />
                        <GridToolbarFilterButton />
                      </GridToolbarContainer>
                    ),
                  }}
                />
                {openMapDialog && (
                  <AddNewLinePopup
                    isOpen={openMapDialog}
                    onClose={() => setOpenMapDialog(false)}
                    handleMapEmailSubmit={handleMapEmailSubmit}
                  />
                )}
                {openEditDialog && (
                  <EditMapEmailPopup
                    isOpen={openEditDialog}
                    onClose={() => setOpenEditDialog(false)}
                    email={emailContent}
                    handleEditMapEmailSubmit={handleEditMapEmailSubmit}
                  />
                )}
                <ConfirmDeleteDialog
                  parent={"map_email"}
                  open={openDeleteDialog}
                  onClose={handleCloseDeleteDialog}
                  onConfirm={handleConfirmDelete}
                />
                <ErrorDialog
                  open={errorDialogOpen}
                  onClose={() => setErrorDialogOpen(false)}
                  message={errorMessage}
                />
                <APIStatusDialog
                  open={apiStatus.open}
                  status={apiStatus.status}
                  message={apiStatus.message}
                />
              </Card>
            </Box>
          </Grid>
        </Grid>
      </DashboardLayout>
    </>
  );
}

export default EmailMapping;
